import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const INITIAL_FORM = {
  name: '', description: '', price: 0, points_amount: 0,
  credits_total: 0, billing_cycle: 'once', package_type: 'one_time',
  validity_days: null, validity_months: null, allowed_service_ids: [], is_active: true,
  // Admin kann pro Abo wählen:
  //  - "monthly_25" = Kalender-basiert (am 25. fix)
  //  - "on_depletion" = Auto-Reload bei Aufbrauch (Default)
  // Customer-Portal zeigt nur den Auto-Reload-Modus klar an —
  // Kunden müssen die Admin-Auswahl nicht selbst treffen.
  renewal_mode: 'on_depletion',
  low_balance_threshold: 0,
};

const PackageFormDialog = ({ open, onOpenChange, editingProduct, productType, services, recurringDiscount = 5, onSaved }) => {
  const [formData, setFormData] = useState(INITIAL_FORM);

  React.useEffect(() => {
    if (!open) return;
    if (editingProduct) {
      setFormData({
        name: editingProduct.name || '',
        description: editingProduct.description || '',
        price: editingProduct.price || 0,
        points_amount: editingProduct.points_amount || 0,
        credits_total: editingProduct.credits_total || 0,
        billing_cycle: editingProduct.billing_cycle || 'once',
        package_type: editingProduct.package_type || 'one_time',
        validity_days: editingProduct.validity_days || null,
        validity_months: editingProduct.validity_months || null,
        allowed_service_ids: editingProduct.allowed_service_ids || [],
        is_active: editingProduct.is_active !== false,
        renewal_mode: editingProduct.renewal_mode || 'on_depletion',
        low_balance_threshold: editingProduct.low_balance_threshold ?? 0,
      });
    } else {
      setFormData({
        ...INITIAL_FORM,
        points_amount: productType === 'points' ? 100 : 0,
        credits_total: productType === 'subscription' ? 10 : 0,
      });
    }
  }, [open, editingProduct, productType]);

  const toggleService = (serviceId) => {
    setFormData(prev => {
      const ids = prev.allowed_service_ids || [];
      return { ...prev, allowed_service_ids: ids.includes(serviceId) ? ids.filter(id => id !== serviceId) : [...ids, serviceId] };
    });
  };

  const handleSave = async () => {
    try {
      const token = localStorage.getItem('token');
      if (productType === 'points') {
        const payload = { name: formData.name, description: formData.description, price: formData.price, points_amount: formData.points_amount, package_type: formData.package_type, validity_days: formData.validity_days, validity_months: formData.validity_months, allowed_service_ids: formData.allowed_service_ids, is_active: formData.is_active };
        if (editingProduct) {
          await axios.put(`${API}/point-packages/${editingProduct.id}`, payload, { headers: { Authorization: `Bearer ${token}` } });
          toast.success('Punkt-Paket aktualisiert');
        } else {
          await axios.post(`${API}/point-packages`, payload, { headers: { Authorization: `Bearer ${token}` } });
          toast.success('Punkt-Paket erstellt');
        }
      } else {
        const payload = {
          name: formData.name,
          description: formData.description,
          price: formData.price,
          credits_total: formData.credits_total,
          billing_cycle: formData.billing_cycle,
          allowed_service_ids: formData.allowed_service_ids,
          is_active: formData.is_active,
          renewal_mode: formData.billing_cycle === 'once' ? 'on_depletion' : formData.renewal_mode,
          low_balance_threshold:
            formData.billing_cycle !== 'once' && formData.renewal_mode === 'on_depletion'
              ? formData.low_balance_threshold
              : null,
        };
        if (editingProduct) {
          await axios.put(`${API}/subscriptions/${editingProduct.id}`, payload, { headers: { Authorization: `Bearer ${token}` } });
          toast.success('Abo aktualisiert');
        } else {
          await axios.post(`${API}/subscriptions`, payload, { headers: { Authorization: `Bearer ${token}` } });
          toast.success('Abo erstellt');
        }
      }
      onSaved();
    } catch (error) {
      toast.error('Fehler beim Speichern');
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{editingProduct ? 'Produkt bearbeiten' : `Neues ${productType === 'points' ? 'Punkt-Paket' : 'Abo'} erstellen`}</DialogTitle>
          <DialogDescription>{editingProduct ? 'Bearbeiten Sie die Produktdetails' : 'Erstellen Sie ein neues Produkt'}</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
            <p className="text-sm text-amber-900">
              <strong>💡 Wichtig — „Erlaubte Services":</strong>
              <br />
              Wähle die Services aus, für die dieses Abo gelten soll. <strong>Nur ausgewählte Services</strong> verbrauchen die Punkte des Abos.
              <br />
              <span className="text-xs italic">
                Beispiel: Beim &bdquo;10er Abo Kryosauna&ldquo; nur die Kryosauna anhaken — der Kunde kann die 10 Punkte dann <strong>nicht</strong> für FLEX EMS oder andere Services verwenden.
              </span>
              <br />
              <span className="text-xs text-gray-600">
                Leer lassen = gilt für <strong>ALLE</strong> Services (Universal-Punkte).
              </span>
            </p>
          </div>

          <div>
            <Label>Name</Label>
            <Input placeholder={productType === 'points' ? 'z.B. 100 Punkte Paket' : 'z.B. 10er-Abo Kryosauna'} value={formData.name} onChange={(e) => setFormData({...formData, name: e.target.value})} data-testid="package-form-name" />
          </div>
          <div>
            <Label>Beschreibung</Label>
            <Textarea placeholder="Beschreibung des Produkts..." value={formData.description} onChange={(e) => setFormData({...formData, description: e.target.value})} />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Preis (CHF)</Label>
              <Input type="number" step="0.01" value={formData.price} onChange={(e) => setFormData({...formData, price: parseFloat(e.target.value) || 0})} data-testid="package-form-price" />
            </div>
            <div>
              <Label>
                {productType === 'points' ? 'Anzahl Punkte' : 'Punkte-Guthaben'}
              </Label>
              <Input
                type="number"
                value={productType === 'points' ? formData.points_amount : formData.credits_total}
                onChange={(e) => setFormData({ ...formData, [productType === 'points' ? 'points_amount' : 'credits_total']: parseInt(e.target.value) || 0 })}
                data-testid="package-form-amount"
              />
              {productType === 'subscription' && (
                <p className="text-xs text-gray-500 mt-1 leading-relaxed">
                  💡 <strong>Punkte ≠ Sessions.</strong> Beispiel: Bei 10 Punkten
                  bekommt der Kunde 10× Kryosauna (1 Pkt/Session) oder
                  3× FLEX EMS (3 Pkt/Session). Die Punkte werden je nach
                  gebuchtem Service abgezogen.
                </p>
              )}
            </div>
          </div>

          {productType === 'points' && (
            <div>
              <Label>Gültigkeit (Monate, optional)</Label>
              <Input
                type="number"
                min="0"
                placeholder="z.B. 12 — leer = unbegrenzt gültig"
                value={formData.validity_months ?? ''}
                onChange={(e) => setFormData({ ...formData, validity_months: e.target.value === '' ? null : (parseInt(e.target.value, 10) || null) })}
                data-testid="package-form-validity-months"
              />
              <p className="text-xs text-gray-500 mt-1">
                Beim Verkauf wird automatisch ein Ablaufdatum gesetzt (Kaufdatum + X Monate).
                Der Kunde erhält 14 Tage vor Ablauf eine Push-Erinnerung; am Ablauftag verfallen
                die Restpunkte (vom Admin reaktivierbar).
              </p>
            </div>
          )}

          {productType === 'subscription' && (
            <div className="space-y-3">
              <div>
                <Label>Abo-Typ</Label>
                <Select value={formData.billing_cycle} onValueChange={(value) => setFormData({...formData, billing_cycle: value})}>
                  <SelectTrigger><SelectValue placeholder="Wählen Sie einen Typ" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="once">10er-Abo (Einmalkauf)</SelectItem>
                    <SelectItem value="monthly">Monatlich wiederkehrend (mit Rabatt)</SelectItem>
                    <SelectItem value="yearly">Jährlich wiederkehrend (mit Rabatt)</SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-xs text-gray-500 mt-1">
                  {formData.billing_cycle === 'once'
                    ? 'Einmaliger Kauf ohne automatische Verlängerung. Credits verfallen wenn das Ablaufdatum erreicht ist.'
                    : `Automatische Verlängerung mit ${recurringDiscount}% Rabatt`}
                </p>
              </div>

              {formData.billing_cycle !== 'once' && (
                <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 space-y-3">
                  <Label className="text-sm font-semibold text-amber-900">
                    Wann wird der Kunde belastet? <span className="text-xs font-normal text-gray-500">(nur Admin)</span>
                  </Label>
                  <div className="space-y-2">
                    <label className="flex items-start gap-3 cursor-pointer p-2 rounded hover:bg-amber-100 transition-colors" data-testid="renewal-mode-on_depletion">
                      <input
                        type="radio"
                        name="renewal_mode"
                        value="on_depletion"
                        checked={formData.renewal_mode === 'on_depletion'}
                        onChange={() => setFormData({...formData, renewal_mode: 'on_depletion'})}
                        className="mt-1"
                      />
                      <div className="flex-1">
                        <div className="text-sm font-medium text-gray-900">
                          ⚡ Verbrauchs-basiert (Auto-Reload bei Aufbrauch) <span className="text-xs text-emerald-600 font-semibold">— Empfohlen</span>
                        </div>
                        <p className="text-xs text-gray-600 mt-1">
                          Belastung erfolgt <strong>sofort wenn Credits aufgebraucht sind</strong>.
                          Kein fester Abrechnungstag. Ideal für aktive Kunden die ohne
                          Unterbrechung trainieren wollen.
                        </p>
                      </div>
                    </label>

                    <label className="flex items-start gap-3 cursor-pointer p-2 rounded hover:bg-amber-100 transition-colors" data-testid="renewal-mode-monthly_25">
                      <input
                        type="radio"
                        name="renewal_mode"
                        value="monthly_25"
                        checked={formData.renewal_mode === 'monthly_25'}
                        onChange={() => setFormData({...formData, renewal_mode: 'monthly_25'})}
                        className="mt-1"
                      />
                      <div className="flex-1">
                        <div className="text-sm font-medium text-gray-900">
                          📅 Kalender-basiert (25. des Monats)
                        </div>
                        <p className="text-xs text-gray-600 mt-1">
                          Belastung erfolgt <strong>jeden 25. fix</strong> — egal ob noch
                          Credits da sind oder nicht. Übrige Credits werden mit den neuen
                          kombiniert. Aufgebrauchte Credits werden NICHT sofort nachgeladen.
                        </p>
                      </div>
                    </label>
                  </div>

                  {formData.renewal_mode === 'on_depletion' && (
                    <div className="pl-7 pt-2 border-t border-amber-200">
                      <Label className="text-xs font-medium text-amber-900">
                        Vorzeitiger Nachlade-Trigger (optional)
                      </Label>
                      <div className="flex items-center gap-2 mt-1">
                        <Input
                          type="number"
                          min="0"
                          max="20"
                          className="w-24"
                          value={formData.low_balance_threshold ?? ''}
                          onChange={(e) => setFormData({
                            ...formData,
                            low_balance_threshold: e.target.value === '' ? null : parseInt(e.target.value, 10)
                          })}
                          data-testid="low-balance-threshold-input"
                        />
                        <span className="text-xs text-gray-600">
                          Punkte übrig → schon dann nachladen (0 = erst bei 0 Punkten)
                        </span>
                      </div>
                      <p className="text-xs text-gray-500 mt-2 italic">
                        Beispiel: Bei „2" wird neu belastet sobald nur noch 2 Punkte übrig sind.
                      </p>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          <div>
            <Label className="text-base font-semibold">Erlaubte Services</Label>
            <p className="text-sm text-gray-600 mb-3">
              {formData.allowed_service_ids.length === 0 ? 'Keine Auswahl = Produkt gilt für ALLE Services' : `${formData.allowed_service_ids.length} Service(s) ausgewählt`}
            </p>
            <div className="border rounded-lg p-4 max-h-60 overflow-y-auto">
              {services.map((service) => {
                const isSelected = formData.allowed_service_ids.includes(service.id);
                return (
                  <div key={service.id} className={`flex items-center justify-between p-3 mb-2 rounded cursor-pointer transition-colors ${isSelected ? 'bg-emerald-50 border border-emerald-200' : 'bg-gray-50 hover:bg-gray-100'}`} onClick={() => toggleService(service.id)}>
                    <div className="flex items-center gap-3">
                      <input type="checkbox" checked={isSelected} onChange={() => {}} className="w-4 h-4" />
                      <div>
                        <p className="font-medium">{service.name}</p>
                        <p className="text-sm text-gray-600">{service.points_cost} Punkte · CHF {(service.price || 0).toFixed(2)}</p>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Abbrechen</Button>
          <Button onClick={handleSave} data-testid="package-form-save">{editingProduct ? 'Aktualisieren' : 'Erstellen'}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default PackageFormDialog;
